import { useMemo, useRef, useState } from "react";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Dialog, { type Ref as DialogRef } from "@/components/Dialog";
import SegmentedButton from "@/components/SegmentedButton";
import { copyTextToClipboard } from "@/components/CopyButton";
import { useSnackbar } from "notistack";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import type { RosterActivityEvent } from "@/features/roster-activity";
import {
  buildRosterCopy,
  buildRosterCopyPreview,
  downloadRosterCopy,
  rosterCopySuccessMessage,
  rosterDownloadSuccessMessage,
  type RosterCopyFormat
} from "../copyText";

type Props = {
  events: RosterActivityEvent[];
};

const FORMAT_OPTIONS = [
  { label: "Text", value: "text" },
  { label: "CSV", value: "csv" },
  { label: "Sheets / Excel", value: "tsv" }
];

const FORMAT_HINTS: Record<RosterCopyFormat, string> = {
  text: "Plain list for notes or messages.",
  csv: "Comma-separated for CSV files.",
  tsv: "Copy: tab-separated for paste into Sheets or Excel. Download: Excel (.xlsx) file."
};

function isRosterCopyFormat(value: string | null): value is RosterCopyFormat {
  return value === "text" || value === "csv" || value === "tsv";
}

export default function CopyActions({ events }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { setError } = useErrorBanner();
  const dialogRef = useRef<DialogRef>(null);
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<RosterCopyFormat>("text");
  const [includeDates, setIncludeDates] = useState(false);

  const preview = useMemo(
    () => buildRosterCopyPreview(events, { format, includeDates }),
    [events, format, includeDates]
  );
  const previewTruncated = events.length > 3;

  const closeDialog = () => {
    dialogRef.current?.close();
  };

  const exportPayload = () => buildRosterCopy(events, { format, includeDates });

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(exportPayload());
      enqueueSnackbar(rosterCopySuccessMessage(format), { variant: "default" });
      closeDialog();
    } catch {
      setError("Failed to copy list to the clipboard.");
    }
  };

  const handleDownload = async () => {
    try {
      await downloadRosterCopy(events, { format, includeDates });
      enqueueSnackbar(rosterDownloadSuccessMessage(format), { variant: "default" });
      closeDialog();
    } catch {
      setError("Failed to download the export file.");
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="roster-activity-copy-actions">
      <Button variant="outlined" icon="content_copy" onClick={() => setOpen(true)}>
        Download / Copy Results
      </Button>
      {open ? (
        <Dialog
          ref={dialogRef}
          open
          headline="Download / Copy Results"
          hasFormElements
          onClosed={() => setOpen(false)}
          className="!h-auto !w-[min(40rem,calc(100vw-3rem))] !min-w-[min(40rem,calc(100vw-3rem))] !max-w-[min(40rem,calc(100vw-3rem))]"
          actions={
            <>
              <Button variant="text" onClick={closeDialog}>
                Cancel
              </Button>
              <Button variant="outlined" icon="download" testId="roster-copy-download" onClick={handleDownload}>
                Download
              </Button>
              <Button onClick={handleCopy}>Copy to clipboard</Button>
            </>
          }
        >
          <div className="box-border flex w-full min-w-0 flex-col gap-6 py-1">
            <div className="flex min-w-0 flex-col gap-2">
              <p className="text-label-large text-sys-brand-on-surface-variant m-0">Format</p>
              <SegmentedButton
                ariaLabel="Copy format"
                options={FORMAT_OPTIONS}
                value={format}
                fullWidth
                onChange={(value) => {
                  if (isRosterCopyFormat(value)) setFormat(value);
                }}
              />
              <p className="text-body-small text-sys-brand-on-surface-variant m-0">{FORMAT_HINTS[format]}</p>
            </div>
            <Checkbox
              name="roster-copy-include-dates"
              label="Include Dates"
              checked={includeDates}
              onChange={setIncludeDates}
            />
            <div className="flex min-w-0 flex-col gap-2">
              <p className="text-label-large text-sys-brand-on-surface-variant m-0">Sample</p>
              <div className="min-w-0 overflow-hidden rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-low">
                <div className="max-h-[7rem] min-w-0 overflow-x-auto overflow-y-auto" data-testid="roster-copy-sample">
                  <pre
                    className="m-0 w-max min-w-full px-3 py-2 text-body-small text-sys-brand-on-surface whitespace-pre"
                    aria-live="polite"
                  >
                    {preview}
                  </pre>
                </div>
                {previewTruncated ? (
                  <p className="m-0 border-t border-sys-brand-outline-variant px-3 py-1.5 text-body-small text-sys-brand-on-surface-variant">
                    Showing the first 3 of {events.length} rows.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
